import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalFonts,
  signalRadii,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  GlassCard,
  Mono,
  ScreenFrame,
  SectionLabel,
  SignalButton,
  StatusPill,
} from '@/common/components/signal';
import { syncStates } from '../fixtures';

/** The four table columns, shared by the header and every row. */
const GRID = '150px 1fr 260px 220px';

const HEADINGS = ['STATE', 'FILE', "HOW IT'S CLASSIFIED", 'WHAT HAPPENS'];

/** The staged change summary, by kind. */
const CHANGES: [string, string, string, string][] = [
  ['＋', signalColors.cyan, '2 new files', '+182 MB'],
  ['↑', signalColors.amber, '1 modified', '18.4 MB'],
  ['−', signalColors.red, '1 removed', '−41 MB'],
];

/** What the browser can and can't do without the desktop agent. */
const CAPABILITIES: [string, string, string, string][] = [
  [
    '✓',
    signalColors.green,
    'Browse files, stream renders, read history',
    'WORKS IN BROWSER',
  ],
  [
    '✕',
    signalText.muted,
    'Check status, commit, locate project',
    'DESKTOP ONLY',
  ],
];

const TOASTS: {
  glyph: string;
  status: 'synced' | 'conflict' | 'behind';
  message: string;
  action: string;
  /** Only the failure's action is coloured — it's the one that must be taken. */
  urgent?: boolean;
}[] = [
  {
    glyph: '✓',
    status: 'synced',
    message: 'Pushed v15 · 4 files',
    action: 'UNDO',
  },
  {
    glyph: '!',
    status: 'conflict',
    message: 'Upload failed — v15 rolled back',
    action: 'RETRY',
    urgent: true,
  },
  {
    glyph: '◴',
    status: 'behind',
    message: '2 files in an unknown state',
    action: 'REVIEW',
  },
];

/** Panel used by the commit summary and the toast column. */
const Panel: React.FC<{
  children: React.ReactNode;
  border?: string;
  background?: string;
  sx?: object;
}> = ({
  children,
  border = signalBorders.default,
  background = signalSurfaces.fillSubtle,
  sx,
}) => (
  <Box
    sx={{
      border: `1px solid ${border}`,
      borderRadius: `${signalRadii['2xl']}px`,
      background,
      padding: '18px',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * 14 — Sync status set.
 *
 * A legend for the one thing the agent is constantly asserting: what state each
 * file is in. The table gives every state its classification rule *and* its
 * consequence, side by side, because "BEHIND" is only meaningful if you know it
 * came from an mtime comparison and that committing will overwrite your local
 * copy. UNKNOWN is the important row — rather than guessing, the agent declines
 * to touch the file, which is what makes the other five states trustworthy.
 */
export const Screen14SyncStatus: React.FC = () => (
  <ScreenFrame
    index="14"
    title="Sync status set"
    note="CLASSIFICATION · COMMIT · RECOVERY"
    bare
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box
        sx={{
          width: 1420,
          maxWidth: '100%',
          borderRadius: `${signalRadii['3xl']}px`,
          overflow: 'hidden',
          border: `1px solid ${signalBorders.medium}`,
          background: signalSurfaces.screen,
          boxShadow: '0 40px 90px -40px rgba(0,0,0,0.95)',
          padding: '24px 26px 28px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <Box>
            <Box
              component="h3"
              sx={{
                margin: 0,
                fontFamily: signalFonts.display,
                fontWeight: 800,
                fontSize: '26px',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              Nightdrive · Sync
            </Box>
            <Mono size={11} sx={{ display: 'block', marginTop: '7px' }}>
              CHECKED 12 S AGO · 412 FILES COMPARED · 4 CHANGES STAGED
            </Mono>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <SignalButton
              variant="secondary"
              sx={{
                padding: '9px 15px',
                borderRadius: `${signalRadii.md}px`,
                background: signalSurfaces.fillActive,
                borderColor: signalBorders.control,
                '&:hover': { background: 'rgba(255,255,255,0.12)' },
              }}
            >
              Re-check status
            </SignalButton>
            <SignalButton
              glow={false}
              sx={{ padding: '9px 16px', borderRadius: `${signalRadii.md}px` }}
            >
              Commit 4 changes → v15
            </SignalButton>
          </Box>
        </Box>

        <Box
          sx={{
            border: `1px solid ${signalBorders.default}`,
            borderRadius: `${signalRadii['2xl']}px`,
            background: signalSurfaces.fillSubtle,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: '16px',
              padding: '11px 16px',
              borderBottom: `1px solid ${signalBorders.divider}`,
            }}
          >
            {HEADINGS.map((heading) => (
              <Mono key={heading} size={10} weight={600} letterSpacing="0.1em">
                {heading}
              </Mono>
            ))}
          </Box>

          {syncStates.map((state) => (
            <Box
              key={state.label}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: '16px',
                alignItems: 'center',
                padding: '13px 16px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                '&:hover': { background: signalSurfaces.fillWeak },
              }}
            >
              <StatusPill
                status={state.status}
                size={9.5}
                sx={{ justifySelf: 'start', padding: '4px 9px', gap: '6px' }}
              >
                <Box component="span" aria-hidden>
                  {state.glyph}
                </Box>
                {state.label}
              </StatusPill>

              <Mono
                size={12.5}
                color={signalText.primary}
                sx={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {state.file}
              </Mono>

              <Box
                sx={{
                  fontSize: '12.5px',
                  color: signalText.secondary,
                  lineHeight: 1.5,
                }}
              >
                {state.rule}
              </Box>
              <Box
                sx={{
                  fontSize: '12.5px',
                  color: signalText.muted,
                  lineHeight: 1.5,
                }}
              >
                {state.action}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginTop: '18px',
          }}
        >
          <Panel>
            <SectionLabel
              size={10}
              sx={{ display: 'block', marginBottom: '14px' }}
            >
              COMMIT MESSAGE
            </SectionLabel>
            <Box
              sx={{
                background: 'rgba(255,106,31,0.06)',
                border: `1px solid ${signalColors.accent}`,
                borderRadius: `${signalRadii.md}px`,
                padding: '12px 14px',
                boxShadow: '0 0 0 3px rgba(255,106,31,0.18)',
                fontSize: '13.5px',
                color: signalText.primary,
                marginBottom: '14px',
              }}
            >
              Vocal comp + new bridge
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              {CHANGES.map(([glyph, color, label, size]) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    fontSize: '12.5px',
                    color: signalText.secondary,
                  }}
                >
                  <Box component="span" sx={{ color, fontSize: '11px' }}>
                    {glyph}
                  </Box>
                  {label}
                  <Mono size={10.5} sx={{ marginLeft: 'auto' }}>
                    {size}
                  </Mono>
                </Box>
              ))}

              {/* The transactional guarantee, stated where the commit is
                  actually made rather than buried in docs. */}
              <Mono
                size={10.5}
                sx={{
                  marginTop: '6px',
                  paddingTop: '12px',
                  borderTop: `1px solid ${signalBorders.divider}`,
                  lineHeight: 1.6,
                }}
              >
                IF ANY UPLOAD FAILS, v15 IS ROLLED BACK — HISTORY STAYS
                CONSISTENT.
              </Mono>
            </Box>
          </Panel>

          <Panel
            border={signalStatus.behind.border}
            background="rgba(245,196,81,0.05)"
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <StatusPill
              status="behind"
              dot
              sx={{
                alignSelf: 'flex-start',
                marginBottom: '14px',
                padding: '5px 10px',
              }}
            >
              FOLDER MOVED
            </StatusPill>
            <Box
              sx={{
                fontFamily: signalFonts.display,
                fontWeight: 700,
                fontSize: '19px',
                letterSpacing: '-0.02em',
                marginBottom: '8px',
              }}
            >
              We can&apos;t find this project on disk
            </Box>
            <Box
              component="p"
              sx={{
                margin: '0 0 14px',
                fontSize: '12.5px',
                lineHeight: 1.6,
                color: signalText.secondary,
              }}
            >
              The last known path no longer exists. Point us at the folder again
              and sync resumes — your cloud versions are untouched.
            </Box>
            <Mono
              size={11}
              sx={{ display: 'block', lineHeight: 1.7, marginBottom: '16px' }}
            >
              LAST KNOWN
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: signalColors.amber,
                  textDecoration: 'line-through',
                }}
              >
                ~/Music/Ableton/Nightdrive Project
              </Box>
            </Mono>
            <Box sx={{ marginTop: 'auto', display: 'flex', gap: '10px' }}>
              <SignalButton
                glow={false}
                sx={{
                  padding: '10px 17px',
                  borderRadius: `${signalRadii.md}px`,
                }}
              >
                Locate project
              </SignalButton>
              <SignalButton
                variant="secondary"
                sx={{
                  padding: '10px 17px',
                  borderRadius: `${signalRadii.md}px`,
                  background: 'transparent',
                  color: signalText.secondary,
                  borderColor: signalBorders.stronger,
                }}
              >
                Download a fresh copy
              </SignalButton>
            </Box>
          </Panel>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          alignItems: 'stretch',
        }}
      >
        <GlassCard
          width={560}
          padding="28px"
          sx={{ background: signalSurfaces.glassStrong }}
        >
          <StatusPill
            status="stems"
            sx={{ marginBottom: '16px', padding: '5px 10px' }}
          >
            WEB · NO BRIDGE
          </StatusPill>
          <Box
            component="h3"
            sx={{
              margin: '0 0 8px',
              fontFamily: signalFonts.display,
              fontWeight: 800,
              fontSize: '26px',
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
            }}
          >
            File sync requires the desktop app
          </Box>
          <Box
            component="p"
            sx={{
              margin: '0 0 20px',
              fontSize: '13px',
              lineHeight: 1.6,
              color: signalText.secondary,
            }}
          >
            The browser can&apos;t read your local session folder. Install
            StudioVault Desktop to sync, commit versions and locate projects —
            browsing and streaming keep working here.
          </Box>

          {/* Says what still works before what doesn't, so the dialog reads as
              a boundary rather than as a paywall. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '11px',
              marginBottom: '22px',
            }}
          >
            {CAPABILITIES.map(([glyph, color, label, note]) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '12.5px',
                  color: signalText.secondary,
                }}
              >
                <Box component="span" sx={{ color, fontSize: '11px' }}>
                  {glyph}
                </Box>
                {label}
                <Mono size={10} color={color} sx={{ marginLeft: 'auto' }}>
                  {note}
                </Mono>
              </Box>
            ))}
          </Box>

          <Box
            sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}
          >
            <SignalButton
              variant="ghost"
              sx={{ padding: '10px 14px', borderRadius: `${signalRadii.md}px` }}
            >
              Not now
            </SignalButton>
            <SignalButton
              glow={false}
              sx={{ padding: '10px 18px', borderRadius: `${signalRadii.md}px` }}
            >
              Get the desktop app
            </SignalButton>
          </Box>
        </GlassCard>

        <Box
          sx={{
            width: 560,
            maxWidth: '100%',
            borderRadius: `${signalRadii['3xl']}px`,
            border: `1px solid ${signalBorders.medium}`,
            background: signalSurfaces.screen,
            boxShadow: '0 30px 70px -36px rgba(0,0,0,0.95)',
            padding: '28px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SectionLabel
            size={10}
            sx={{ display: 'block', marginBottom: '16px' }}
          >
            TOASTS
          </SectionLabel>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            {TOASTS.map((toast) => {
              const tone = signalStatus[toast.status];
              return (
                <Box
                  key={toast.message}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '11px',
                    border: `1px solid ${tone.border}`,
                    background: tone.bg,
                    borderRadius: '13px',
                    padding: '13px 15px',
                  }}
                >
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      width: 20,
                      height: 20,
                      flex: 'none',
                      borderRadius: '999px',
                      background: tone.bg,
                      color: tone.fg,
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {toast.glyph}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      fontSize: '13px',
                      color: signalText.primary,
                    }}
                  >
                    {toast.message}
                  </Box>
                  <Mono
                    size={10}
                    weight={toast.urgent ? 600 : 500}
                    color={toast.urgent ? tone.fg : signalText.muted}
                  >
                    {toast.action}
                  </Mono>
                </Box>
              );
            })}
          </Box>

          <SectionLabel
            size={10}
            sx={{ display: 'block', marginTop: '22px', marginBottom: '12px' }}
          >
            APP UPDATE
          </SectionLabel>
          <Box
            sx={{
              border: '1px solid rgba(255,106,31,0.32)',
              background: 'rgba(255,106,31,0.06)',
              borderRadius: '13px',
              padding: '15px',
              display: 'flex',
              alignItems: 'center',
              gap: '13px',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: signalText.primary,
                }}
              >
                StudioVault 2.5.0 is ready
              </Box>
              <Mono size={10.5} sx={{ display: 'block', marginTop: '4px' }}>
                DOWNLOADED · RESTART TO INSTALL
              </Mono>
            </Box>
            <SignalButton
              glow={false}
              sx={{
                padding: '9px 15px',
                fontSize: '12.5px',
                borderRadius: '9px',
              }}
            >
              Restart
            </SignalButton>
          </Box>
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen14SyncStatus;
